import { Drawer, Stack, Text, Badge, Group, Button, Timeline, Title } from '@mantine/core';
import { IconCheck, IconAlertTriangle, IconBan } from '@tabler/icons-react';
import { useModerationHistory } from '@/hooks/useModeration';
import { reasonLabel } from './ModerationPage';
import type { ModerationResult, ModerationDecision } from '@/types/moderation';

interface Props {
  item: ModerationResult | null;
  onClose: () => void;
  onOverride: (item: ModerationResult) => void;
}

const decisionIcon: Record<ModerationDecision, typeof IconCheck> = {
  ALLOW: IconCheck,
  REVIEW: IconAlertTriangle,
  HOLD: IconBan,
};

const decisionColor: Record<ModerationDecision, string> = {
  ALLOW: 'green',
  REVIEW: 'yellow',
  HOLD: 'red',
};

export function ModerationDetailDrawer({ item, onClose, onOverride }: Props) {
  const { data: history = [] } = useModerationHistory(item?.offerId ?? 0);

  if (!item) return null;

  return (
    <Drawer
      opened={!!item}
      onClose={onClose}
      title={`Offer #${item.offerId}`}
      position="right"
      size="md"
    >
      <Stack>
        <Group>
          <Badge color={decisionColor[item.decision]} size="lg" variant="light">
            {item.decision}
          </Badge>
          <Text size="sm" c="dimmed">
            {item.source} — {item.ruleId || 'kein Regel-Match'}
          </Text>
        </Group>

        <Stack gap="xs">
          <Text fw={500}>Produktname</Text>
          <Text>{item.offer?.name || '—'}</Text>
        </Stack>

        {item.offer?.description && (
          <Stack gap="xs">
            <Text fw={500}>Beschreibung</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {item.offer.description.replace(/<[^>]+>/g, '')}
            </Text>
          </Stack>
        )}

        {item.offer && (
          <Group>
            <Stack gap={2}>
              <Text fw={500} size="sm">Preis</Text>
              <Text>{item.offer.value.toFixed(2)} €</Text>
            </Stack>
            <Stack gap={2}>
              <Text fw={500} size="sm">UVP</Text>
              <Text>{item.offer.srp.toFixed(2)} €</Text>
            </Stack>
            <Stack gap={2}>
              <Text fw={500} size="sm">Status</Text>
              <Text>{item.offer.status}</Text>
            </Stack>
          </Group>
        )}

        <Stack gap="xs">
          <Text fw={500}>Grund</Text>
          <Text>{reasonLabel(item.reason)}</Text>
        </Stack>

        {item.confidence > 0 && (
          <Stack gap="xs">
            <Text fw={500}>KI-Konfidenz</Text>
            <Text>{(item.confidence * 100).toFixed(0)}%</Text>
          </Stack>
        )}

        <Button onClick={() => onOverride(item)} mt="md">
          Override
        </Button>

        {history.length > 0 && (
          <>
            <Title order={5} mt="lg">Verlauf</Title>
            <Timeline active={0} bulletSize={24}>
              {history.map((entry) => {
                const Icon = decisionIcon[entry.newDecision];
                return (
                  <Timeline.Item
                    key={entry.id}
                    bullet={<Icon size={14} />}
                    color={decisionColor[entry.newDecision]}
                    title={`${entry.previousDecision} → ${entry.newDecision}`}
                  >
                    <Text size="xs" c="dimmed">
                      {new Date(entry.createdAt).toLocaleString('de-DE')} — {entry.reviewerId || entry.source}
                    </Text>
                    {entry.reason && <Text size="sm">{reasonLabel(entry.reason)}</Text>}
                  </Timeline.Item>
                );
              })}
            </Timeline>
          </>
        )}
      </Stack>
    </Drawer>
  );
}
